import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import './MyAccountModal.css';

interface SectionProps {
  onBack: () => void;
}

const BillingSubscription = ({ onBack }: SectionProps) => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(() => null);
  const [usage, setUsage] = useState(() => null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (!user) return;

        const { data, error } = await supabase
          .from('users')
          .select('stripe_customer_id, stripe_subscription_id, email')
          .eq('auth_id', user.id)
          .single();

        if (error) throw error;

        const subscriptionResponse = await fetch(
          `https://askrexi.comverex.ai/get-subscription-status?customer_id=${data.stripe_customer_id}`
        );
        const usageResponse = await fetch(
          `https://askrexi.comverex.ai/get-usage-summary?customer_id=${data.stripe_customer_id}`
        );

        const subJson = await subscriptionResponse.json();
        const usageJson = await usageResponse.json();

        setSubscription(subJson);
        setUsage(usageJson);
      } catch (err) {
        console.error('Error fetching billing data:', err);
        setMessage('Unable to load billing details.');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const openStripePortal = async () => {
    try {
      setPortalLoading(true);
      setMessage('');

      // Get current authenticated user
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) {
        setMessage('User not authenticated.');
        return;
      }

      // Fetch stripe_customer_id from Supabase
      const { data, error } = await supabase
        .from('users')
        .select('stripe_customer_id')
        .eq('auth_id', user.id)
        .single();

      if (error || !data?.stripe_customer_id) {
        console.error('Stripe customer ID not found:', error);
        setMessage('Unable to open billing portal — missing customer record.');
        return;
      }

      // Call backend to create a Stripe billing portal session
      const response = await fetch('https://askrexi.comverex.ai/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: data.stripe_customer_id }),
      });

      const json = await response.json();

      if (json.url) {
        // Open the Stripe billing portal in a new secure tab
        window.open(json.url, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Stripe portal creation failed:', json);
        setMessage('Unable to open billing portal.');
      }
    } catch (err) {
      console.error('Error opening Stripe portal:', err);
      setMessage('Unable to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-section">
        <div className="loading">Loading billing details...</div>
      </div>
    );
  }

  return (
    <div className="modal-section">
      <h2>Billing & Subscription</h2>

      {subscription ? (
        <>
          <div className="section-block">
            <p><strong>Base Rate:</strong> $95 / month</p>
            <p><strong>Usage:</strong> $2 per report upload</p>
          </div>

          <div className="section-block">
            <p><strong>Reports this month:</strong> {usage?.count ?? 0}</p>
            <p><strong>Usage total:</strong> ${usage?.total?.toFixed(2) ?? '0.00'}</p>
            <p className="info-text small">
              Note: Your payment method’s billing period may not align exactly with month end.
            </p>
          </div>

          <div className="section-block">
            <p>
              Usage charges reset automatically each billing cycle. Reports uploaded near
              the end of your billing period will appear on the next month’s invoice.
            </p>
          </div>

          <div className="section-block">
            <h3>Payment Method</h3>
            <p>Expires: {subscription.card_exp || 'N/A'}</p>
            <p>Billing Email: {subscription.email || 'N/A'}</p>

            <div className="section-block">
              <p>
                Click Open Billing Portal to view payment history, update your payment method, or cancel your subscription.
              </p>
            </div>

            {/* Stripe portal + cancel buttons, hidden when cancel modal is open */}
            {!message.includes('portal') && (
              <>
                <button
                  onClick={openStripePortal}
                  disabled={portalLoading}
                  className="primary-button"
                >
                  {portalLoading ? 'Opening...' : 'Open Billing Portal'}
                </button>
              </>
            )}

            {/* Show message if portal fails */}
            {message && message.includes('portal') && (
              <div className="info-banner" style={{ fontSize: 'inherit' }}>
                Unable to open billing portal, please try again later or contact support@comverex.com.
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="error-text">No subscription found. Please try again later or contact support@comverex.com</div>
      )}

      <div className="modal-footer">
        <br />
        <button className="my-account-back-btn" onClick={onBack}>
          ← Back to My Account
        </button>
      </div>
    </div>
  );
};

export default BillingSubscription;
